import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { supabase } from '../lib/supabase';
import { Spinner } from '../components/Spinner';

/**
 * Mi Perfil (2026-07-21): identidad del usuario + su ACTIVIDAD de escritura en SharePoint.
 * La app escribe en Graph con identidad propia (autoría plana en SharePoint), así que esta
 * auditoría es el registro fiel de QUIÉN hizo cada cambio desde la plataforma.
 */

interface Actividad {
  accion: 'subir' | 'reemplazar' | 'carpeta' | 'renombrar' | 'eliminar' | string;
  ruta: string;
  detalle: { nuevo_nombre?: string; size?: number; notificados?: string[] } | null;
  created_at: string;
}

const ACCION: Record<string, { icono: string; label: string; tinte: string }> = {
  subir: { icono: '↑', label: 'Subió', tinte: 'bg-emerald-50 text-emerald-700 ring-emerald-300' },
  reemplazar: { icono: '⟳', label: 'Reemplazó', tinte: 'bg-celeste/20 text-azul-profundo ring-celeste/60' },
  carpeta: { icono: '📁', label: 'Creó carpeta', tinte: 'bg-celeste/20 text-azul-profundo ring-celeste/60' },
  renombrar: { icono: '✎', label: 'Renombró', tinte: 'bg-amber-50 text-amber-700 ring-amber-300' },
  eliminar: { icono: '🗑', label: 'Eliminó', tinte: 'bg-red-50 text-red-700 ring-red-300' },
};

export default function MiPerfil() {
  const [correo, setCorreo] = useState('');
  const [actividad, setActividad] = useState<Actividad[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ms, setMs] = useState<{ conectado: boolean; ms_email: string | null; connected_at: string | null } | null>(null);
  const [msOcupado, setMsOcupado] = useState(false);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setCorreo(data.user?.email ?? ''));
    apiFetch<{ actividad: Actividad[] }>('/api/sharepoint/auditoria')
      .then((d) => setActividad(d.actividad))
      .catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar la actividad.'));
    apiFetch<{ conectado: boolean; ms_email: string | null; connected_at: string | null }>('/api/msauth/estado')
      .then(setMs)
      .catch(() => setMs({ conectado: false, ms_email: null, connected_at: null }));
  }, []);

  async function conectarMicrosoft() {
    setMsOcupado(true);
    try {
      const { url } = await apiFetch<{ url: string }>('/api/msauth/authorize');
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo iniciar la conexión con Microsoft.');
      setMsOcupado(false);
    }
  }

  async function desconectarMicrosoft() {
    setMsOcupado(true);
    try {
      await apiFetch('/api/msauth', { method: 'DELETE' });
      setMs({ conectado: false, ms_email: null, connected_at: null });
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo desconectar.'); }
    finally { setMsOcupado(false); }
  }

  // Cambio de contraseña (2026-07-24): re-autentica con la clave actual y actualiza.
  const [claveActual, setClaveActual] = useState('');
  const [claveNueva, setClaveNueva] = useState('');
  const [claveConfirma, setClaveConfirma] = useState('');
  const [claveOcupado, setClaveOcupado] = useState(false);
  const [claveMsg, setClaveMsg] = useState<{ ok: boolean; texto: string } | null>(null);

  async function cambiarClave() {
    setClaveMsg(null);
    if (claveNueva.length < 8) { setClaveMsg({ ok: false, texto: 'La clave nueva debe tener al menos 8 caracteres.' }); return; }
    if (claveNueva !== claveConfirma) { setClaveMsg({ ok: false, texto: 'La confirmación no coincide.' }); return; }
    setClaveOcupado(true);
    try {
      const { error: eAuth } = await supabase.auth.signInWithPassword({ email: correo, password: claveActual });
      if (eAuth) { setClaveMsg({ ok: false, texto: 'La clave actual no es correcta.' }); return; }
      const { error: eUpd } = await supabase.auth.updateUser({ password: claveNueva });
      if (eUpd) { setClaveMsg({ ok: false, texto: eUpd.message }); return; }
      setClaveActual(''); setClaveNueva(''); setClaveConfirma('');
      setClaveMsg({ ok: true, texto: 'Contraseña actualizada ✓' });
    } finally { setClaveOcupado(false); }
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="tarjeta flex items-center gap-4 p-5">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-azul-profundo text-lg font-medium text-white">
          {(correo[0] ?? 'U').toUpperCase()}
        </span>
        <div>
          <p className="text-[15px] font-medium text-azul-noche">{correo || '…'}</p>
          <p className="text-[12px] font-light text-gris">Usuario de la Plataforma de Agentes AMBEC</p>
        </div>
      </section>

      {/* Wave 3: cuenta Microsoft — el explorador hereda TUS permisos de SharePoint */}
      <section className="tarjeta p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-medium text-azul-noche">Cuenta Microsoft (SharePoint)</h2>
            <p className="mt-1 text-[12px] font-normal leading-relaxed text-gris">
              {ms?.conectado
                ? <>Conectada como <strong className="font-medium text-azul-noche">{ms.ms_email}</strong> — el explorador muestra exactamente lo que tus permisos de SharePoint permiten, y tus cambios salen con tu autoría real.</>
                : <>Conecta tu cuenta corporativa (<strong className="font-medium">{correo || 'tu email'}</strong>) para ver SharePoint con tus permisos reales. Sin conectar, el explorador y los proyectos DIA no están disponibles.</>}
            </p>
          </div>
          {ms === null ? <Spinner tamano="sm" /> : ms.conectado ? (
            <span className="flex shrink-0 items-center gap-2">
              <span className="chip bg-emerald-50 text-emerald-700 ring-emerald-300">● Conectada</span>
              <button type="button" onClick={() => void desconectarMicrosoft()} disabled={msOcupado} className="btn-fantasma !text-[12.5px] disabled:opacity-50">Desconectar</button>
            </span>
          ) : (
            <button type="button" onClick={() => void conectarMicrosoft()} disabled={msOcupado} className="btn-primario shrink-0 disabled:opacity-50">
              {msOcupado ? 'Redirigiendo…' : 'Conectar mi cuenta Microsoft'}
            </button>
          )}
        </div>
      </section>

      {/* Cambio de contraseña */}
      <section className="tarjeta p-5">
        <h2 className="text-sm font-medium text-azul-noche">Cambiar contraseña</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <div>
            <label className="etiqueta" htmlFor="clave-actual">Clave actual</label>
            <input id="clave-actual" type="password" autoComplete="current-password" value={claveActual} onChange={(e) => setClaveActual(e.target.value)} className="campo" />
          </div>
          <div>
            <label className="etiqueta" htmlFor="clave-nueva">Clave nueva</label>
            <input id="clave-nueva" type="password" autoComplete="new-password" value={claveNueva} onChange={(e) => setClaveNueva(e.target.value)} className="campo" />
          </div>
          <div>
            <label className="etiqueta" htmlFor="clave-confirma">Confirmar clave nueva</label>
            <input id="clave-confirma" type="password" autoComplete="new-password" value={claveConfirma} onChange={(e) => setClaveConfirma(e.target.value)} className="campo" />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => void cambiarClave()} disabled={claveOcupado || !claveActual || !claveNueva} className="btn-primario disabled:opacity-50">
            {claveOcupado ? 'Guardando…' : 'Actualizar contraseña'}
          </button>
          {claveMsg && (
            <p role={claveMsg.ok ? 'status' : 'alert'} className={`text-[12.5px] font-normal ${claveMsg.ok ? 'text-emerald-700' : 'text-red-600'}`}>{claveMsg.texto}</p>
          )}
        </div>
      </section>

      <section className="tarjeta p-5">
        <h2 className="text-sm font-medium text-azul-noche">Mi actividad en SharePoint</h2>
        <p className="mt-1 text-[12px] font-normal leading-relaxed text-gris">
          Los cambios hechos desde la plataforma se registran aquí a tu nombre (en SharePoint el autor
          aparece como la aplicación). Últimos 100 movimientos.
        </p>
        {error && <p role="alert" className="alerta-error mt-3">{error}</p>}
        {actividad === null ? (
          <div className="flex justify-center py-8"><Spinner tamano="md" /></div>
        ) : actividad.length === 0 ? (
          <p className="mt-4 py-6 text-center text-sm font-light text-gris">
            Aún no registras cambios — sube, renombra o elimina archivos desde AMBEC → SharePoint.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-50">
            {actividad.map((a, i) => {
              const meta = ACCION[a.accion] ?? { icono: '·', label: a.accion, tinte: 'bg-slate-100 text-slate-700 ring-slate-300' };
              return (
                <li key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
                  <span className={`chip shrink-0 ${meta.tinte}`}>{meta.icono} {meta.label}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-normal text-texto" title={a.ruta}>
                      {a.ruta.split('/').pop()}
                      {a.detalle?.nuevo_nombre ? <span className="text-gris"> → {a.detalle.nuevo_nombre}</span> : null}
                    </span>
                    <span className="block truncate text-[10.5px] font-light text-gris">{a.ruta.split('/').slice(0, -1).join(' › ') || 'raíz'}</span>
                  </span>
                  {(a.detalle?.notificados?.length ?? 0) > 0 && (
                    <span className="chip shrink-0 bg-emerald-50 text-emerald-700 ring-emerald-300" title={`Andrés re-estudió: ${a.detalle!.notificados!.join(', ')}`}>
                      📚 {a.detalle!.notificados!.length}
                    </span>
                  )}
                  <span className="shrink-0 text-[11px] font-light text-gris">
                    {new Date(a.created_at).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
